import { useState } from "react";
import type { MfgMaterial, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { EditModelForm, NewModelForm } from "./ManufacturingModelsMetaForms";
import { ManufacturingModelsAppliedNamedVariantsStrip } from "./ManufacturingModelsAppliedNamedVariantsStrip";
import { ManufacturingModelsAttachmentsEditor } from "./ManufacturingModelsAttachmentsEditor";
import { ManufacturingModelsBomEditor } from "./ManufacturingModelsBomEditor";
import { ManufacturingModelsNeedsReviewBanner } from "./ManufacturingModelsNeedsReviewBanner";
import { ManufacturingModelsPendingLinesEditor } from "./ManufacturingModelsPendingLinesEditor";
import { ManufacturingModelsRestartModal } from "./ManufacturingModelsRestartModal";
import { ManufacturingModelsVariantTransformEditor } from "./ManufacturingModelsVariantTransformEditor";
import type { ManufacturingModelsAttachmentSectionModel } from "./useManufacturingModelsAttachmentSection";
import type { useManufacturingModelsTab } from "./useManufacturingModelsTab";

type ModelTab = ReturnType<typeof useManufacturingModelsTab>;

export function ManufacturingModelsDetailColumn({
  isCreate,
  m,
  materials,
  recipes,
  recipeGroups,
  packageAttachments,
  canDeactivateModel,
  canPermanentlyDelete,
  canEditCatalogCode,
  selectedRecipe,
  onDictationComplete,
  onRequestDeactivate,
  onRequestPermanentDelete,
  refreshPendingCounts,
  orgSlotLabels,
  onSlotLabelsRefresh,
  onOpenVariantsWorkspace,
  onOpenPartUses,
  pendingCount = 0,
  onReviewConfirmed,
}: {
  isCreate: boolean;
  m: ModelTab;
  materials: MfgMaterial[];
  recipes: MfgRecipeSummary[];
  recipeGroups: ReadonlyArray<{ id: string; code: string; name: string }>;
  packageAttachments: ManufacturingModelsAttachmentSectionModel;
  canDeactivateModel: boolean;
  canPermanentlyDelete: boolean;
  canEditCatalogCode: boolean;
  selectedRecipe: MfgRecipeSummary | null;
  onDictationComplete: (transcript: string) => void;
  onRequestDeactivate: () => void;
  onRequestPermanentDelete: () => void;
  refreshPendingCounts: () => void;
  orgSlotLabels: readonly string[];
  onSlotLabelsRefresh: () => void;
  onOpenVariantsWorkspace: (namedVariantId?: string) => void;
  onOpenPartUses?: () => void;
  pendingCount?: number;
  onReviewConfirmed?: () => void;
}) {
  const [restartOpen, setRestartOpen] = useState(false);

  return (
    <div className="space-y-4">
      {isCreate && m.creating ? (
        <NewModelForm
          newCode={m.newCode}
          newName={m.newName}
          newKind={m.newKind}
          newTagsInput={m.newTagsInput}
          newNotes={m.newNotes}
          newRecipeGroupId={m.newRecipeGroupId}
          recipeGroups={recipeGroups}
          saveBusy={m.saveBusy}
          duplicateDraft={m.duplicateDraft}
          onSmartDuplicateApplied={m.applySmartDuplicatePatch}
          onSmartDuplicateError={(msg) => m.setLocalError(msg || null)}
          setNewCode={m.setNewCode}
          setNewName={m.setNewName}
          setNewKind={m.setNewKind}
          setNewTagsInput={m.setNewTagsInput}
          setNewNotes={m.setNewNotes}
          setNewRecipeGroupId={m.setNewRecipeGroupId}
          onCreateRecipe={m.onCreateRecipe}
          onDictationComplete={onDictationComplete}
        />
      ) : null}

      {!m.creating && m.selectedId && m.detail ? (
        <>
          {isCreate ? (
            <EditModelForm
              editCode={m.editCode}
              editName={m.editName}
              editKind={m.editKind}
              editTagsInput={m.editTagsInput}
              editNotes={m.editNotes}
              editRecipeGroupId={m.editRecipeGroupId}
              editWholesale={m.editWholesale}
              editMsrp={m.editMsrp}
              sourceTags={m.detail.recipe.tags}
              slotLabels={m.detail.lines.map((line) => line.slot_label)}
              recipeGroups={recipeGroups}
              saveBusy={m.saveBusy}
              detailBusy={m.detailBusy}
              isActive={m.detail.recipe.is_active}
              canDeactivate={canDeactivateModel}
              canPermanentlyDelete={canPermanentlyDelete}
              canEditCode={canEditCatalogCode}
              setEditCode={m.setEditCode}
              setEditName={m.setEditName}
              setEditKind={m.setEditKind}
              setEditTagsInput={m.setEditTagsInput}
              setEditNotes={m.setEditNotes}
              setEditRecipeGroupId={m.setEditRecipeGroupId}
              setEditWholesale={m.setEditWholesale}
              setEditMsrp={m.setEditMsrp}
              onSaveRecipeMeta={(rules) => void m.onSaveRecipeMeta(canEditCatalogCode, rules)}
              onClose={m.closeSelectedModel}
              onRestart={() => setRestartOpen(true)}
              onRequestDeactivate={onRequestDeactivate}
              onRequestPermanentDelete={() => {
                if (selectedRecipe) onRequestPermanentDelete();
              }}
            />
          ) : null}

          {m.detailBusy ? (
            <div className="text-sm text-gray-500 dark:text-zinc-400">Loading detail…</div>
          ) : null}

          {!m.detailBusy ? (
            <>
              {isCreate && m.detail.recipe.needs_review ? (
                <ManufacturingModelsNeedsReviewBanner
                  recipeId={m.selectedId}
                  pendingCount={pendingCount}
                  onConfirmed={() => {
                    m.reloadDetail();
                    refreshPendingCounts();
                    onReviewConfirmed?.();
                  }}
                  onError={(msg) => m.setLocalError(msg)}
                />
              ) : null}
              {isCreate ? (
                <ManufacturingModelsPendingLinesEditor
                  recipeId={m.selectedId}
                  materials={materials}
                  onChanged={() => {
                    m.reloadDetail();
                    refreshPendingCounts();
                  }}
                  onError={(msg) => m.setLocalError(msg)}
                />
              ) : null}
              {m.detail.recipe.kind === "variant" ? (
                <ManufacturingModelsVariantTransformEditor
                  recipeId={m.selectedId}
                  tags={m.detail.recipe.tags}
                  orgSlotLabels={orgSlotLabels}
                  readOnly={!isCreate}
                  onError={(msg) => m.setLocalError(msg)}
                  onSaved={() => void m.reloadDetail()}
                />
              ) : null}
              <ManufacturingModelsBomEditor
                selectedId={m.selectedId}
                detail={m.detail}
                materials={materials}
                recipes={recipes}
                orgSlotLabels={orgSlotLabels}
                onError={(msg) => m.setLocalError(msg)}
                onSaved={() => {
                  m.reloadDetail();
                  onSlotLabelsRefresh();
                }}
                readOnly={!isCreate}
                onOpenPartUses={onOpenPartUses}
              />
              <ManufacturingModelsAttachmentsEditor
                selectedId={m.selectedId}
                a={packageAttachments}
                orgSlotLabels={orgSlotLabels}
              />
              <ManufacturingModelsAppliedNamedVariantsStrip
                parentRecipeId={m.selectedId}
                componentRecipeIds={(m.detail?.lines ?? [])
                  .map((ln) => ln.component_recipe_id)
                  .filter((id): id is string => typeof id === "string" && id.length > 0)}
                defaultNamedVariantApplicationIds={
                  m.detail?.recipe.default_named_variant_application_ids ?? []
                }
                onOpenVariantsWorkspace={onOpenVariantsWorkspace}
                onDefaultsSaved={() => m.reloadDetail()}
              />
            </>
          ) : null}
        </>
      ) : null}

      {!m.creating && !m.selectedId ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-400">
          Choose a model from the list{isCreate ? " or create a new one" : ""}.
        </div>
      ) : null}

      {restartOpen && m.detail ? (
        <ManufacturingModelsRestartModal
          detail={m.detail}
          recipes={recipes}
          materials={materials}
          orgSlotLabels={orgSlotLabels}
          onClose={() => setRestartOpen(false)}
          onDone={() => {
            setRestartOpen(false);
            m.reloadDetail();
            onSlotLabelsRefresh();
          }}
        />
      ) : null}
    </div>
  );
}
